// TEMP: simulate production hide rules on localhost for manual testing.
// Roll back after tests: set to false or remove this constant + early return below.
const FORCE_PRODUCTION_RELEASE_FOR_TESTING = false;

export default class PanelsConfig {
  /**
   * We distinguish environments by the Git tag convention used in `currentVersion`:
   * - production: `webui-v1.30.0`
   * - develop/test: `webui-v1.30.0.develop`, `webui-v1.30.0.test`
   * - localhost: `VERSION` (placeholder during local dev)
   */
  static isProductionReleaseTag() {
    if (FORCE_PRODUCTION_RELEASE_FOR_TESTING) return true;

    // Never treat localhost as production, even if a prod-like version is injected.
    try {
      const hostname = window?.location?.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1') return false;
    } catch {
      // ignore (SSR / non-browser)
    }

    // GitLab CI replaced tag (same source as `HitHeader.vue`).
    let v = '';
    try {
      // eslint-disable-next-line global-require
      const {currentVersion} = require('../version.js');
      v = String(currentVersion || '').trim();
    } catch {
      v = '';
    }

    // Local dev placeholder.
    if (v === 'VERSION') return false;

    // Extract semver + optional suffix from `webui-v1.30.0(.develop|.test|...)`
    const match = v.match(/webui-v(\d+\.\d+\.\d+(\.\w+)?)/);
    if (!match?.[1]) return false;

    // Production is the *no suffix* case: `webui-v1.30.0`
    return /^\d+\.\d+\.\d+$/.test(match[1]);
  }

  static HIDDEN_MODULE_KEYS_IN_PRODUCTION = ['tracking-base'];

  static isModuleHiddenInProduction(designationKey) {
    if (!designationKey || !PanelsConfig.isProductionReleaseTag()) return false;
    return PanelsConfig.HIDDEN_MODULE_KEYS_IN_PRODUCTION.includes(
      designationKey,
    );
  }

  static filterPanels(panels) {
    if (!PanelsConfig.isProductionReleaseTag()) return panels;
    // Hide tracking-related panels on production releases.
    return (panels || []).filter(
      (p) => !['position-tracker', 'poi'].includes(p.id),
    );
  }

  static ADDRESSDETAIL = [
    {id: 'information', key: 'hit-app.common.information'},
    {id: 'communication', key: 'hit-base.common.communication-mode'},
    {id: 'action', key: 'hit-base.common.action', moduleKeys: ['action-base']},
    {id: 'memo', key: 'hit-base.common.memo'},
    {
      id: 'transaction',
      key: 'hit-app.transaction.general-title',
      moduleKeys: ['transaction-base'],
    },
    {id: 'role', key: 'hit-base.common.role'},
    {id: 'identifier', key: 'hit-app.common.identifier'},
    {id: 'form', key: 'hit-base.common.form', moduleKeys: ['form-base']},
    {id: 'files', key: 'hit-app.common.files'},
    {id: 'bank-account', key: 'hit-app.common.bank-account'},
    {
      id: 'billing-parameters',
      key: 'hit-base.common.configuration',
      moduleKeys: ['transaction-base'],
    },
    {id: 'contact', key: 'hit-app.common.contact'},
    {id: 'poi', key: 'hit-components.map.pois'},
    {
      id: 'projectpart',
      key: 'hit-base.common.project-part',
      moduleKeys: ['project-base'],
    },
    {
      id: 'purchase',
      key: 'hit-base.common.purchase',
      moduleKeys: ['purchase-base'],
    },
  ];

  static STAFFDETAIL = [
    {id: 'information', key: 'hit-app.common.information'},
    {id: 'communication', key: 'hit-base.common.communication-mode'},
    {id: 'action', key: 'hit-base.common.action', moduleKeys: ['action-base']},
    {id: 'cost', key: 'hit-app.common.costs'},
    {id: 'form', key: 'hit-base.common.form', moduleKeys: ['form-base']},
    {id: 'files', key: 'hit-app.common.files'},
    {
      id: 'confidentialdata',
      key: 'hit-app.common.confidential-data',
      moduleKeys: ['staff-confidential-data'],
    },
    {id: 'memo', key: 'hit-base.common.memo'},
    {id: 'poi', key: 'hit-components.map.pois'},
    {id: 'default-values', key: 'hit-app.common.configuration'},
    {
      id: 'timeslot',
      key: 'hit-base.common.planning',
      moduleKeys: ['planning-base'],
    },
    {id: 'competence', key: 'hit-base.common.competence'},
    {id: 'nsso', key: 'hit-app.common.nsso-history'},
  ];

  static RESOURCEDETAIL = [
    {id: 'information', key: 'hit-app.common.information'},
    {id: 'memo', key: 'hit-base.common.memo'},
    {id: 'action', key: 'hit-base.common.action', moduleKeys: ['action-base']},
    {
      id: 'packaging',
      key: 'hit-app.resource.packaging',
      moduleKeys: ['resource-packaging-supplier'],
    },
    {id: 'price', key: 'hit-app.common.price'},
    {
      id: 'supplier',
      key: 'hit-app.common.supplier',
      moduleKeys: ['resource-packaging-supplier'],
    },
    {id: 'stock', key: 'hit-base.common.stock'},
    {id: 'role', key: 'hit-base.common.role'},
    {id: 'form', key: 'hit-base.common.form', moduleKeys: ['form-base']},
    {id: 'files', key: 'hit-app.common.files'},
    {
      id: 'technical-specifications',
      key: 'hit-app.common.technical-specifications',
    },
  ];

  static PURCHASEDETAIL = [
    {id: 'information', key: 'hit-app.common.information'},
    {
      id: 'suppliers',
      key: 'hit-app.price-request.supplier-prices',
      moduleKeys: ['purchase-order-and-price-requests'],
    },
    {id: 'memo', key: 'hit-base.common.memo'},
    {id: 'files', key: 'hit-app.common.files'},
    {id: 'delivery-notes', key: 'hit-app.common.delivery-note'},
  ];

  static EQUIPMENTDETAIL = [
    {id: 'information', key: 'hit-app.common.information'},
    {id: 'cost', key: 'hit-app.common.costs'},
    {id: 'action', key: 'hit-base.common.action', moduleKeys: ['action-base']},
    {id: 'details', key: 'hit-app.common.details'},
    {id: 'maintenance', key: 'hit-app.equipment.maintenance'},
    {id: 'memo', key: 'hit-base.common.memo'},
    {id: 'role', key: 'hit-base.common.role'},
    {id: 'form', key: 'hit-base.common.form', moduleKeys: ['form-base']},
    {id: 'files', key: 'hit-app.common.files'},
    {
      id: 'timeslot',
      key: 'hit-base.common.planning',
      moduleKeys: ['planning-base'],
    },
    {id: 'position-tracker', key: 'hit-base.common.position-tracker'},
  ];

  static PROJECTDETAIL = [
    {id: 'information', key: 'hit-app.common.information'},
    {id: 'quotation-parts', key: 'hit-app.project.quotation-part'},
    {id: 'work-parts', key: 'hit-app.project.work-part'},
    {id: 'siteaddress', key: 'hit-app.common.siteaddress'},
    {id: 'poi', key: 'hit-components.map.pois'},
    {id: 'memo', key: 'hit-base.common.memo'},
    {id: 'role', key: 'hit-base.common.role'},
    {id: 'form', key: 'hit-base.common.form', moduleKeys: ['form-base']},
    {id: 'files', key: 'hit-app.common.files'},
    {
      id: 'analysis',
      key: 'hit-app.common.analysis',
      moduleKeys: ['project-detailed-analysis'],
    },
    {id: 'nsso-history', key: 'hit-app.common.nsso-history'},
  ];

  static PROJECTPARTDETAIL = [
    {id: 'information', key: 'hit-app.common.information'},
    {id: 'cost', key: 'hit-app.common.costs'},
    {id: 'address', key: 'hit-app.common.siteaddress'},
    {id: 'action', key: 'hit-base.common.action', moduleKeys: ['action-base']},
    {
      id: 'period-billing',
      key: 'hit-app.common.periodic-billing',
      moduleKeys: ['project-periodic-billing'],
    },
    {id: 'payment-schedule', key: 'hit-app.common.payment-schedule'},
    {id: 'memo', key: 'hit-base.common.memo'},
    {
      id: 'transaction',
      key: 'hit-app.transaction.general-title',
      moduleKeys: ['transaction-base'],
    },
    {id: 'role', key: 'hit-base.common.role'},
    {id: 'form', key: 'hit-base.common.form', moduleKeys: ['form-base']},
    {id: 'files', key: 'hit-app.common.files'},
    {
      id: 'analysis',
      key: 'hit-app.common.analysis',
      moduleKeys: ['project-detailed-analysis'],
    },
    {
      id: 'technical-specifications',
      key: 'hit-app.common.technical-specifications',
    },
    {
      id: 'timeslot',
      key: 'hit-base.common.planning',
      moduleKeys: ['planning-base'],
    },
    {id: 'contact', key: 'hit-app.common.contact'},
    {
      id: 'purchase',
      key: 'hit-app.common.purchase',
      moduleKeys: ['purchase-base'],
    },
  ];

  static ACTIONDETAIL = [
    {id: 'information', key: 'hit-app.common.information'},
    {id: 'description', key: 'hit-base.common.memo'},
    {id: 'history', key: 'hit-app.common.history'},
    {id: 'linkedEntity', key: 'hit-app.common.linked-entity'},
    {id: 'files', key: 'hit-app.common.files'},
  ];

  static EQUIPMENTMAINTENANCEDETAIL = [
    {id: 'information', key: 'hit-app.common.information'},
    {id: 'resource', key: 'hit-app.equipment-maintenance.maintenance-resource'},
    {id: 'memo', key: 'hit-base.common.memo'},
    {id: 'files', key: 'hit-app.common.files'},
  ];
  /**
   * Returns a list with the IDs of all the panels that are defined for the passed view
   */
  static getPanelIds(viewName) {
    if (!viewName) return [];
    return PanelsConfig.filterPanels(
      PanelsConfig?.[viewName.toUpperCase()] || [],
    ).map((panel) => panel.id);
  }

  static getAllViewPanels(viewName) {
    if (!viewName) return [];
    return PanelsConfig.filterPanels(
      PanelsConfig?.[viewName.toUpperCase()] || [],
    );
  }
}
